import React from 'react';
import { cn } from '../../../utils/cn';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../context/AuthContext';

const AccountSidebar = ({ 
  activeSection, 
  onSectionChange, 
  isOpen, 
  onClose, 
  userData 
}) => {
  const { user, canSwitchToAdminView, switchToAdminView, logout } = useAuth();

  const navigationItems = [
    {
      id: 'profile',
      label: 'Profile Settings',
      icon: 'User',
      description: 'Personal information and photo'
    },
    {
      id: 'orders',
      label: 'Order History',
      icon: 'Package',
      description: 'View and track your orders'
    },
    {
      id: 'addresses',
      label: 'Address Book',
      icon: 'MapPin',
      description: 'Manage shipping addresses'
    },
    {
      id: 'payments',
      label: 'Payment Methods',
      icon: 'CreditCard',
      description: 'Saved cards and billing'
    },
    {
      id: 'preferences',
      label: 'Preferences',
      icon: 'Settings',
      description: 'Notifications and privacy'
    },
    {
      id: 'security',
      label: 'Security Settings',
      icon: 'Shield',
      description: 'Password and security'
    }
  ];

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-64 lg:w-[280px] bg-card border-r border-border flex-shrink-0">
        {/* Desktop Header */}
        <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <h2 className="text-lg font-semibold text-foreground truncate">Account Menu</h2>
          <button
            onClick={logout}
            className="p-2 rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors flex-shrink-0"
            aria-label="Logout"
          >
            <Icon name="LogOut" size={20} />
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
          {navigationItems?.map((item) => (
            <button
              key={item?.id}
              onClick={() => onSectionChange(item?.id)}
              className={cn(
                "w-full flex items-start space-x-3 p-3 rounded-lg text-left transition-colors min-w-0 cursor-pointer",
                activeSection === item?.id
                  ? "bg-primary/10 text-primary border border-primary/20" :"text-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <Icon 
                name={item?.icon} 
                size={20} 
                className={cn(
                  "mt-0.5 flex-shrink-0",
                  activeSection === item?.id ? "text-primary" : "text-muted-foreground"
                )}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {item?.label}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {item?.description}
                </p>
              </div>
              {activeSection === item?.id && (
                <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
              )}
            </button>
          ))}
        </nav>

        {/* User Profile Section with Admin Switch */}
        <div className="p-4 border-t border-border space-y-2 flex-shrink-0">
          {canSwitchToAdminView() && (
            <button
              onClick={switchToAdminView}
              className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 transition-smooth min-w-0 cursor-pointer"
            >
              <Icon name="Shield" size={16} className="flex-shrink-0" />
              <span className="truncate">Switch to Admin View</span>
              <Icon name="ArrowRight" size={14} className="ml-auto flex-shrink-0" />
            </button>
          )}
          
          <button
            onClick={logout}
            title="Sign out"
            aria-label="Sign out"
            className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg min-w-0 hover:bg-accent hover:text-accent-foreground transition-colors group text-left"
          >
            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="User" size={16} className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground truncate">
                {user?.name || 'User'}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {user?.email || ''}
              </div>
            </div>
            <Icon name="LogOut" size={16} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
        />
      )}

      {/* Mobile Sidebar */}
      <div className={cn(
        "fixed lg:hidden inset-y-0 left-0 z-50 w-80 max-w-[85vw] bg-white border-r border-border transform transition-transform duration-300 ease-in-out overflow-hidden",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full pt-20 overflow-hidden">
          {/* Mobile Header */}
          <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
            <h2 className="text-lg font-semibold text-foreground truncate">Account Menu</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors flex-shrink-0"
              aria-label="Close menu"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* User Profile Summary */}
          <div className="p-6 border-b border-border flex-shrink-0">
            <div className="flex items-center space-x-4 min-w-0">
              <div className="relative flex-shrink-0">
                <img
                  src={userData?.profileImage}
                  alt={userData?.profileImageAlt}
                  className="w-12 h-12 rounded-full object-cover border-2 border-primary/10"
                />
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {userData?.name}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {userData?.email}
                </p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-border/50">
              <div className="text-center min-w-0">
                <p className="text-lg font-semibold text-foreground truncate">
                  {userData?.totalOrders}
                </p>
                <p className="text-xs text-muted-foreground truncate">Orders</p>
              </div>
              <div className="text-center min-w-0">
                <p className="text-lg font-semibold text-foreground truncate">
                  {userData?.loyaltyPoints?.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground truncate">Points</p>
              </div>
            </div>
          </div>

          {/* Navigation Menu */}
          <div className="flex-1 overflow-y-auto">
            <nav className="p-4 space-y-2">
              {navigationItems?.map((item) => (
                <button
                  key={item?.id}
                  onClick={() => onSectionChange(item?.id)}
                  className={cn(
                    "w-full flex items-start space-x-3 p-3 rounded-lg text-left transition-colors min-w-0 cursor-pointer",
                    activeSection === item?.id
                      ? "bg-primary/10 text-primary border border-primary/20" :"text-foreground hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <Icon 
                    name={item?.icon} 
                    size={20} 
                    className={cn(
                      "mt-0.5 flex-shrink-0",
                      activeSection === item?.id ? "text-primary" : "text-muted-foreground"
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {item?.label}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {item?.description}
                    </p>
                  </div>
                  {activeSection === item?.id && (
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Bottom Section removed — tier system not yet wired */}
        </div>
      </div>
    </>
  );
};

export default AccountSidebar;